<template>
  <div
    :class="preview ? 'map-crosshair--preview' : 'map-crosshair'"
    :style="{ color }"
    aria-hidden="true"
  >
    <svg
      class="map-crosshair__svg"
      viewBox="0 0 48 48"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <!-- 1: fine crosshair — thin cross with a centre gap + tiny centre dot -->
      <template v-if="variant === 1">
        <line x1="24" y1="4" x2="24" y2="18" />
        <line x1="24" y1="30" x2="24" y2="44" />
        <line x1="4" y1="24" x2="18" y2="24" />
        <line x1="30" y1="24" x2="44" y2="24" />
        <circle cx="24" cy="24" r="1.5" fill="currentColor" stroke="none" />
      </template>

      <!-- 2: circle with a plus inside, arms touching the circle -->
      <template v-else-if="variant === 2">
        <circle cx="24" cy="24" r="14" />
        <line x1="24" y1="10" x2="24" y2="38" />
        <line x1="10" y1="24" x2="38" y2="24" />
      </template>

      <!-- 3: reticle — circle with a cross whose arms extend past it -->
      <template v-else-if="variant === 3">
        <circle cx="24" cy="24" r="10" />
        <line x1="24" y1="4" x2="24" y2="44" />
        <line x1="4" y1="24" x2="44" y2="24" />
      </template>

      <!-- 4: small hollow ring only (~8px diameter) -->
      <template v-else-if="variant === 4">
        <circle cx="24" cy="24" r="4" />
      </template>

      <!-- 5: chevron burst — four solid wedges pointing inward, centre empty -->
      <template v-else-if="variant === 5">
        <polygon points="20,8 28,8 24,18" fill="currentColor" stroke="none" />
        <polygon points="20,40 28,40 24,30" fill="currentColor" stroke="none" />
        <polygon points="8,20 8,28 18,24" fill="currentColor" stroke="none" />
        <polygon points="40,20 40,28 30,24" fill="currentColor" stroke="none" />
      </template>

      <!-- 6: aircraft-style — circle, dashed horizontal line, caret below centre -->
      <template v-else-if="variant === 6">
        <circle cx="24" cy="24" r="12" />
        <line x1="4" y1="24" x2="44" y2="24" stroke-dasharray="3 3" />
        <polyline points="20,34 24,30 28,34" />
      </template>
    </svg>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import type { StyleIndex } from '@/utils/mapInfoBarSettings';

defineProps({
  /** Positional 1..6, matches the apps' picker (see mapInfoBarSettings StyleIndex).
   *  Named `variant` because `style` is a reserved attribute on Vue components. */
  variant: {
    type: Number as PropType<StyleIndex>,
    required: true,
  },
  /** Hex colour string, bound onto the root so `currentColor` resolves in the SVG. */
  color: {
    type: String,
    required: true,
  },
  /** true renders a plain fill-parent thumbnail (settings dialog); false renders the
   * absolutely-centred, pointer-events-none overlay used on the live map. */
  preview: {
    type: Boolean,
    default: false,
  },
});
</script>

<style scoped lang="scss">
.map-crosshair {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 50;
  width: 48px;
  height: 48px;

  .map-crosshair__svg {
    display: block;
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 0 1px rgba(255, 255, 255, .8));
  }
}

.map-crosshair--preview {
  display: inline-block;
  width: 100%;
  height: 100%;

  .map-crosshair__svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}
</style>
